import { Injectable, effect, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { RouteReuseService } from '@unified-ui/shared/services';

import { Observable, catchError, from, tap, throwError } from 'rxjs';
import { Md5 } from 'ts-md5';

import { AuthSession, FetchUserAttributesOutput, ResetPasswordOutput, SignInOutput, confirmResetPassword, fetchAuthSession, fetchUserAttributes, resetPassword, signIn } from 'aws-amplify/auth';
import { Dexie } from 'dexie';
import { CookieService } from 'ngx-cookie-service';
import { authResetAction, authSetAuthenticated, authSetEmailHash, authSetOrgId, authSetOrgName, authSetUiAnalytics, authSetUserEmail, authSetUserName, authSetUserType } from './auth-store';
import { AuthTokensService } from './auth-tokens.service';
import { TOKEN_TYPE } from './types/tokens.type';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private _authStore = inject(Store);
    private _routeReuseService = inject(RouteReuseService);
    private _authTokensService = inject(AuthTokensService);
    private _cookieService = inject(CookieService);

    protected static readonly DB_NAME_PLANNING = 'planning';
    protected static readonly DB_NAME_MEDIASCOPE = 'mediascope';
    protected static readonly DB_NAME_MARKET_OVERVIEW = 'market-overview';

    constructor() {
        effect(() => {
            if (this._authTokensService.tokensReady$()) {
                this.setAuthStoreParams();
            }
        });
    }

    /**
     * Confirms a password reset with the provided username, new password, and confirmation code.
     *
     * @param {string} username - The username of the user.
     * @param {string} newPassword - The new password of the user.
     * @param {string} confirmationCode - The confirmation code for the password reset.
     * @returns {Observable<void>} - An observable that completes when the password reset is confirmed.
     */
    public confirmResetPassword(username: string, newPassword: string, confirmationCode: string): Observable<void> {
        return from(
            confirmResetPassword({
                username,
                newPassword,
                confirmationCode
            })
        );
    }

    /**
     * Fetches the authentication session of the current user. It includes the tokens stored and the update for the saved tokens.
     *
     * @returns {Observable<AuthSession>} - An observable that emits the authentication session.
     */
    public getAuthSession(): Observable<AuthSession> {
        return from(fetchAuthSession());
    }

    /**
     * Fetches the attributes of the current user.
     *
     * @returns {Observable<FetchUserAttributesOutput>} - An observable that emits the user attributes.
     */
    public getUser(): Observable<FetchUserAttributesOutput> {
        return from(fetchUserAttributes()).pipe(
            tap(res => {
                const userName = `${res.given_name} ${res.family_name}`;
                this._authStore.dispatch(authSetUserName({ userName: userName }));
                this._authStore.dispatch(authSetUserEmail({ email: res.email ?? '' }));
                this._authStore.dispatch(authSetAuthenticated({ authenticated: true }));
                if (res.email) {
                    this._authStore.dispatch(authSetEmailHash({ emailHash: Md5.hashStr(res.email) }));
                    this._authStore.dispatch(authSetUserType({ email: res.email }));
                }
            }),
            catchError(error => {
                this._authStore.dispatch(authResetAction());
                return throwError(() => error);
            })
        );
    }

    /**
     * Signs out the current user and cleans local storage
     */
    public logOut(): void {
        try {
            this._routeReuseService.clearStoredRoutes();
        } catch (e) {
            console.error(e);
            // TODO Handle case where strategy isn't configurable
        }

        const keysToSave = ['theme'];
        this._clearLocalStorageByPrefixes(keysToSave);
        this._clearReportsFromLocalStorageAndIndexedDB();
        this._authStore.dispatch(authResetAction());
        this._cookieService.deleteAll();
        this._authTokensService.loginNavigation();
    }

    /**
     * Logs in a user with the provided username and password.
     *
     * @param {string} username - The username of the user.
     * @param {string} password - The password of the user.
     * @returns {Observable<SignInOutput>} - An observable that emits the sign in output.
     */
    public login(username: string, password: string): Observable<SignInOutput> {
        return from(signIn({ username, password }));
    }

    /**
     * Initiates a password reset for the user with the provided username.
     *
     * @param {string} username - The username of the user.
     * @returns {Observable<ResetPasswordOutput>} - An observable that emits the reset password output.
     */
    public resetPassword(username: string): Observable<ResetPasswordOutput> {
        return from(resetPassword({ username }));
    }

    /**
     * Clears the local storage apart from the keysToSave.
     * @private
     * @param keysToSave
     */
    private _clearLocalStorageByPrefixes(keysToSave: string[]): void {
        const keys = Object.keys(localStorage);
        const keysToRemove = keys.filter(key => !keysToSave.includes(key));
        for (const key of keysToRemove) {
            localStorage.removeItem(key);
        }
    }

    /**
     * Sets the needed tokens information for enabling ui Analytics.
     */
    public setAuthStoreParams(): void {
        const orgId = this._authTokensService.getAuthToken(TOKEN_TYPE.ORG_ID) ?? '';
        const orgList = this._authTokensService.getAuthToken(TOKEN_TYPE.ORG_LIST) ?? '';
        const uiAnalytics = this._authTokensService.getAuthToken(TOKEN_TYPE.UI_ANALYTICS) === 'true' ? true : false;
        this._authStore.dispatch(authSetUiAnalytics({ uiAnalytics: uiAnalytics }));
        this._authStore.dispatch(authSetOrgId({ orgId: orgId }));
        this._authStore.dispatch(authSetOrgName({ orgName: JSON.parse(orgList)[orgId] }));
    }

    private async _clearReportsFromLocalStorageAndIndexedDB(): Promise<void> {
        const databases = [AuthService.DB_NAME_PLANNING, AuthService.DB_NAME_MEDIASCOPE, AuthService.DB_NAME_MARKET_OVERVIEW];
        for (const dbName of databases) {
            try {
                await Dexie.delete(dbName);
                console.log(`🚀 ~ AuthService ~ clearReportsFromLocalStorageAndIndexedDB: Successfully deleted database: ${dbName}`);
            } catch (error) {
                console.error(`🚀 ~ AuthService ~ clearReportsFromLocalStorageAndIndexedDB: Failed to delete database ${dbName}:`, error);
            }
        }
    }
}
